/**
 * Usage Tracker
 * 
 * Tracks expense patterns for smart defaults and suggestions.
 * Separated from QuickEntryService to break circular dependency:
 * - Database layer → Usage Tracker (records patterns)
 * - QuickEntryService → Database layer (reads patterns)
 * 
 * This module is injected into the database layer to record usage
 * without creating a circular import.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Record amount usage for future suggestions
 * 
 * Called after successfully adding an expense.
 * Stores recent amounts for smart amount suggestions.
 * 
 * @param supabase - Supabase client instance
 * @param userId - User ID
 * @param amount - Expense amount
 * @param currency - Currency code
 */
export async function recordAmountUsage(
    supabase: SupabaseClient,
    userId: string,
    amount: number,
    currency: string
): Promise<void> {
    try {
        // Insert will trigger auto-cleanup via database trigger
        await supabase.from('recent_amounts').insert({
            user_id: userId,
            amount,
            currency,
        });
    } catch (error) {
        // Log but don't fail the expense creation
        console.warn('Failed to record amount usage:', error);
    }
}

/**
 * Update category usage pattern based on time
 * 
 * Called after successfully adding an expense with a category.
 * Learns when certain categories are typically used (e.g., "food" at noon).
 * 
 * @param supabase - Supabase client instance
 * @param userId - User ID
 * @param categoryId - Category ID
 */
export async function updateCategoryPattern(
    supabase: SupabaseClient,
    userId: string,
    categoryId: string
): Promise<void> {
    try {
        const now = new Date();
        const hourOfDay = now.getHours();
        const dayOfWeek = now.getDay();

        // Try to find existing pattern
        const { data: existing } = await supabase
            .from('user_expense_patterns')
            .select('id, frequency_count')
            .eq('user_id', userId)
            .eq('category_id', categoryId)
            .eq('hour_of_day', hourOfDay)
            .eq('day_of_week', dayOfWeek)
            .maybeSingle();

        if (existing) {
            // Update existing pattern (increment frequency)
            await supabase
                .from('user_expense_patterns')
                .update({
                    frequency_count: existing.frequency_count + 1,
                    last_used_at: now.toISOString(),
                })
                .eq('id', existing.id);
        } else {
            // Insert new pattern
            await supabase.from('user_expense_patterns').insert({
                user_id: userId,
                category_id: categoryId,
                hour_of_day: hourOfDay,
                day_of_week: dayOfWeek,
                frequency_count: 1,
            });
        }
    } catch (error) {
        // Log but don't fail the expense creation
        console.warn('Failed to update category pattern:', error);
    }
}
